/**
 * The internal catalog — products we invent and force into the databases'
 * answers.
 *
 * This panel creates and lists them; everything else (the fabricated per-catalog
 * codes, the OEs, the cross links) is edited on the product's own page, because
 * an internal product IS a product — the graph does not treat it as a special
 * case, and neither should the console.
 */
import { useState } from 'react';
import { useApi } from '../lib/api';
import { useList, useResource } from '../lib/hooks';
import { useNav } from '../lib/router';
import type { ProductRow } from '../lib/types';
import { qs } from '../lib/util';
import {
  Banner,
  Chip,
  DataBlock,
  Empty,
  ErrorBox,
  Field,
  Loading,
  Modal,
  Pager,
  SourceChip,
  Switch,
  Table,
  Toolbar,
} from '../ui';

const LIMIT = 50;

interface SettingsResp {
  internal: { enabled: boolean; inject: Record<string, boolean>; mark: boolean; markField: string };
  sources: string[];
}

export default function Internal() {
  const { request } = useApi();
  const { openProduct } = useNav();
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState('');
  const [existing, setExisting] = useState<number | null>(null);

  const { data, loading, error, reload } = useList<ProductRow>(
    `/v1/products${qs({ kind: 'internal', q, limit: LIMIT, offset })}`,
  );
  const settings = useResource<SettingsResp>('/v1/admin/settings');
  const s = settings.data?.internal;

  async function save(form: Record<string, string>) {
    setErr('');
    const r = await request('POST', '/v1/admin/internal', {
      brand: form.brand,
      article: form.article,
      ...(form.name ? { name: form.name } : {}),
    });
    if (r.status >= 400) {
      const data = r.data as { error?: string; existingId?: number } | null;
      setErr(String(data?.error ?? `HTTP ${r.status}`));
      // A catalogue already has this code: the way in is to manage THAT product.
      setExisting(typeof data?.existingId === 'number' ? data.existingId : null);
      return;
    }
    setExisting(null);
    setAdding(false);
    reload();
    openProduct((r.data as { id: number }).id);
  }

  async function manageExisting(id: number) {
    const r = await request('POST', `/v1/admin/products/${id}/manage`, { managed: true });
    if (r.status >= 400) {
      setErr(String((r.data as { error?: string })?.error ?? `HTTP ${r.status}`));
      return;
    }
    setErr('');
    setExisting(null);
    setAdding(false);
    openProduct(id);
  }

  async function toggle(id: number, enabled: boolean) {
    await request('PUT', `/v1/admin/internal/${id}`, { enabled });
    reload();
  }

  async function remove(id: number) {
    if (!window.confirm('Delete this internal product? Its aliases, OE links and cross links go with it.')) return;
    await request('DELETE', `/v1/admin/internal/${id}`);
    reload();
  }

  const items = data?.items ?? [];

  return (
    <>
      <Banner icon="✦">
        Products we invent. Each carries a code we fabricate <i>per database</i>, and is injected into that database's
        results — in <span className="mono">/v1</span> and, unless you turn it off, in the raw proxy too.
      </Banner>

      {s && !s.enabled && (
        <div className="mb-3.5 rounded border border-warn bg-panel2 px-3 py-2 text-[12.5px] text-warn">
          The internal catalog is <b>globally off</b> — nothing below is being injected anywhere. Turn it on in{' '}
          <b>Settings</b>.
        </div>
      )}

      <Toolbar>
        <input
          className="input mono"
          style={{ width: 220 }}
          placeholder="Brand or article…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOffset(0);
          }}
        />
        <div className="flex-1" />
        {s && (
          <span className="text-[12.5px] text-faint">
            injecting into:{' '}
            {settings.data!.sources.filter((src) => s.inject[src] !== false).join(', ') || 'nothing'}
            {s.mark ? ` · marked as ${s.markField}` : ' · unmarked'}
          </span>
        )}
        <button className="btn btn-sm btn-primary" onClick={() => setAdding(true)}>
          + New product
        </button>
      </Toolbar>

      {(error || err) && <ErrorBox>{err || error}</ErrorBox>}


      <DataBlock
        title="Internal products"
        sub={data ? `${data.total} product${data.total === 1 ? '' : 's'}` : undefined}
        raw={data}
        right={data ? <Pager total={data.total} limit={LIMIT} offset={offset} onOffset={setOffset} /> : undefined}
      >
        {loading ? (
          <Loading />
        ) : items.length === 0 ? (
          <Empty>No internal products yet.</Empty>
        ) : (
          <Table cols={['Brand', 'Article', 'Name', 'Injected as', 'OEs', 'Cross', 'Enabled', '']}>
            {items.map((p) => (
              <tr key={p.id}>
                <td className="mono">{p.brandNorm}</td>
                <td className="mono">{p.articleNorm}</td>
                <td>{p.name ?? '—'}</td>
                <td>
                  <div className="flex gap-1">
                    {p.aliasSources.length === 0 ? (
                      <span className="text-faint" title="No per-catalog code yet — open it to add one">
                        no codes yet
                      </span>
                    ) : (
                      p.aliasSources.map((x) => <SourceChip key={x} source={x} />)
                    )}
                  </div>
                </td>
                <td className="num">{p.oeCount}</td>
                <td className="num">{p.crossCount}</td>
                <td>
                  {p.enabled ? (
                    <Chip className="border-ok text-ok">on</Chip>
                  ) : (
                    <Chip className="border-line2 text-faint">off</Chip>
                  )}
                </td>
                <td>
                  <div className="flex gap-1.5">
                    <button
                      className="btn btn-sm"
                      title="Its identity, description, attributes, per-catalog codes, OEs and equivalences"
                      onClick={() => openProduct(p.id)}
                    >
                      Open / edit
                    </button>
                    <button className="btn btn-sm" onClick={() => toggle(p.id, !p.enabled)}>
                      {p.enabled ? 'Disable' : 'Enable'}
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={() => remove(p.id)}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </DataBlock>

      <div className="mt-1 text-[11.5px] text-faint">
        The per-catalog codes are <b>invented</b> — they must not collide with a code a real catalog uses. Creating one
        that does is refused.
      </div>

      {adding && (
        <NewProduct
          onCancel={() => {
            setAdding(false);
            setErr('');
            setExisting(null);
          }}
          onSave={save}
          error={err}
          existing={existing}
          onManage={manageExisting}
        />
      )}
    </>
  );
}

function NewProduct({
  onCancel,
  onSave,
  error,
  existing,
  onManage,
}: {
  onCancel: () => void;
  onSave: (form: Record<string, string>) => void;
  error: string;
  /** The catalogue product that already uses the code, when that is why we refused. */
  existing: number | null;
  onManage: (id: number) => void;
}) {
  const [form, setForm] = useState<Record<string, string>>({ brand: '', article: '', name: '' });
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Modal title="New internal product" onClose={onCancel}>
      <div className="flex flex-col gap-2.5">
        <Field label="Brand">
          <input className="input mono" value={form.brand} onChange={(e) => set('brand', e.target.value)} autoFocus />
        </Field>
        <Field label="Article number">
          <input className="input mono" value={form.article} onChange={(e) => set('article', e.target.value)} />
        </Field>
        <Field label="Name">
          <input className="input" value={form.name} onChange={(e) => set('name', e.target.value)} />
        </Field>
        <p className="text-[11.5px] text-faint">
          This is the product's own identity. The codes it pretends to have in Kromeda and TecDoc are added on the next
          screen.
        </p>
        {error && <div className="text-[12px] text-bad">{error}</div>}
        {existing !== null && (
          <div className="flex flex-col gap-1.5 rounded border border-line bg-panel2 p-2.5 text-[12px]">
            <span className="text-muted">
              That code already belongs to a catalogue product. Instead of inventing a new one, you can manage product
              #{existing} as ours: its catalogue data stays as it is, ours (name, photos, description, family) is added on
              top, and it is injected wherever the catalogue leaves the answer empty.
            </span>
            <div>
              <button className="btn btn-sm btn-primary" onClick={() => onManage(existing)}>
                Manage #{existing} as ours
              </button>
            </div>
          </div>
        )}
        <div className="mt-1 flex justify-end gap-2">
          <button className="btn btn-sm" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="btn btn-sm btn-primary"
            disabled={!form.brand.trim() || !form.article.trim()}
            onClick={() => onSave(form)}
          >
            Create
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** A switch with a label and an explanation of what turning it off actually does. */
function Toggle({
  on,
  onChange,
  label,
  hint,
  muted,
}: {
  on: boolean;
  onChange: (v: boolean) => void;
  label: string;
  hint: string;
  muted?: boolean;
}) {
  return (
    <div className={muted ? 'opacity-50' : undefined}>
      <div className="flex items-center gap-2">
        <Switch on={on} onChange={(v) => !muted && onChange(v)} />
        <span className="text-[12.5px]">{label}</span>
      </div>
      <div className="ml-11 text-[11.5px] text-faint">{hint}</div>
    </div>
  );
}

/** The injection switches. Rendered inside Settings, kept here next to what it governs. */
export function InjectionSettings() {
  const { request } = useApi();
  const { data, loading, reload } = useResource<SettingsResp>('/v1/admin/settings');
  const [busy, setBusy] = useState(false);

  async function put(patch: Record<string, unknown>) {
    setBusy(true);
    await request('PUT', '/v1/admin/settings', patch);
    setBusy(false);
    reload();
  }

  if (loading || !data) return <Loading />;
  const s = data.internal;

  return (
    <div className="blk p-4">
      <div className="mb-3 text-[13px] font-semibold">Internal catalog injection</div>
      <div className="flex flex-col gap-3">
        <Toggle
          on={s.enabled}
          muted={busy}
          onChange={(v) => put({ enabled: v })}
          label="Enabled"
          hint="Master switch. Off = internal products appear nowhere, as if the catalog did not exist."
        />
        {data.sources.map((src) => (
          <Toggle
            key={src}
            on={s.inject[src] !== false}
            muted={busy || !s.enabled}
            onChange={(v) => put({ inject: { [src]: v } })}
            label={`Inject into ${src}`}
            hint={`Add internal products to ${src}'s results — including the raw proxy, where they appear as native ${src} rows.`}
          />
        ))}
        <Toggle
          on={s.mark}
          muted={busy || !s.enabled}
          onChange={(v) => put({ mark: v })}
          label={`Mark injected rows (${s.markField})`}
          hint="Adds a field naming the origin, so a consumer can always tell an injected row from a real one."
        />
      </div>
      {!s.mark && s.enabled && (
        <div className="mt-3 rounded border border-warn bg-panel2 px-3 py-2 text-[12px] text-warn">
          Injected rows are currently <b>unmarked</b> — consumers of the raw proxy cannot tell them from real upstream
          rows.
        </div>
      )}
    </div>
  );
}
